import { UserState } from '../models/state.model';

/**
 * Splits a combined "Bahnhofstrasse 12" the way the backend does.
 *
 * The form takes ONE street field, matching the personal form, but the booking API keeps street
 * and house number as separate required fields — so the split happens here, before sending. The
 * pattern is the one BexioHelper.php:189 and AppointmentsService use, so a row created from this
 * form splits identically to a snapshotted or Bexio-synced one.
 *
 * Returns an empty `street_number` when the value carries no number; the form treats that as
 * invalid rather than sending it, because the API requires the number.
 */
export function splitStreetAndHouseNumber(combined: string): { street: string; street_number: string } {
  const value = combined.trim();

  // Ordered, because the patterns overlap. Each needs BOTH a name and a number, which is what
  // the API requires — a value carrying only one of them yields an empty number and the form
  // then blocks, rather than sending half an address.
  const patterns: RegExp[] = [
    // "Bahnhofstrasse 12" / "Bahnhofstrasse 12a" / "Hauptstrasse 3-5" / "Rue du Marché 3bis"
    /^(.+?)[\s,]+(\d+[a-zA-Z0-9\-/]*)$/,
    // "221B Baker Street" — the ordinary en_US/en_GB order
    /^(\d+[a-zA-Z0-9\-/]*)[\s,]+(.+)$/,
    // "Bahnhofstr.12" — no separator at all, common in DE/CH
    /^(.*\D)(\d+[a-zA-Z0-9\-/]*)$/,
  ];

  for (const [index, pattern] of patterns.entries()) {
    const match = value.match(pattern);
    if (!match) {
      continue;
    }
    // the second pattern captures the number first
    const [street, street_number] = index === 1
      ? [match[2].trim(), match[1].trim()]
      : [match[1].trim(), match[2].trim()];

    if (street && street_number) {
      return { street, street_number };
    }
  }

  return { street: value, street_number: '' };
}

/**
 * The invoice address may only be optional when the backend has a complete address to snapshot.
 * The booking form showing the address fields is not enough — they are required only when the
 * partner also marks them required, so a shown-but-blank address would be snapshotted empty and
 * the partner could not invoice the booking. Keying on what the booker actually entered covers
 * both cases, and is shared by every page that renders the form.
 */
export function isInvoiceAddressMandatoryFor(
  needCustomerAddress: boolean | undefined,
  userState: UserState | null | undefined
): boolean {
  if (needCustomerAddress !== true) {
    return true;
  }

  return !(userState?.street?.trim() && userState?.zip?.trim() && userState?.city?.trim());
}
